// app/providers/posthog-provider.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense, useState } from "react";
import { usePostHog } from "posthog-js/react";

import posthog from "posthog-js";
import { classifyAiReferral, claimAiReferral } from "@/lib/ai-referrals";
import { PostHogProvider as PHProvider } from "posthog-js/react";
let initialization: Promise<Pick<typeof posthog, "capture">> | null = null;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return;
    const landing = window.location.href,
      referrer = document.referrer;
    if (!initialization)
      initialization = new Promise((resolve) => {
        posthog.init(key, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
          person_profiles: "identified_only",
          capture_pageview: false,
          autocapture: false,
          disable_session_recording: true,
          loaded: (client) => resolve(client),
        });
      });
    void initialization.then((client) => {
      if (!mounted) return;
      setReady(true);
      const referral = classifyAiReferral(landing, referrer);
      if (referral && claimAiReferral("posthog", referral.ai_provider))
        client.capture("ai_referral", referral);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PHProvider client={posthog}>
      {ready && <SuspendedPostHogPageView />}
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      const search = searchParams.toString();
      if (search) {
        url += "?" + search;
      }
      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
